/**
 * One puppy.
 *
 * A route and not a panel under the litter's board, which is what this used to
 * be. The panel was honest while the only thing a puppy had was four fields to
 * correct; it stops being honest the moment a puppy has a *history* — weights, a
 * growth curve, worming dates, the vet's notes — and none of that fits under a
 * grid of its siblings. So the tile navigates here, and this is the screen that
 * grows.
 *
 * **The litter is a breadcrumb, not a card.** It was drawn here in full once —
 * the same block the litter page draws — but a puppy's own screen repeating its
 * litter's facts back at it was noise the breadcrumb already said; the litter
 * itself is one tap away.
 *
 * **The photographs here are a line, not an album.** They used to be an album:
 * uploaded one at a time into a flat array with no dates on it, which could not
 * answer the question a photographic archive exists for — what did this puppy
 * look like at ten days. So this screen draws the same timeline the litter's
 * page does, through the same component, filtered to this one puppy: the
 * sessions it was actually in, its own frames out of each, and none of the
 * litter's group shots, which are of nobody in particular. See
 * components/gallery/PhotoTimeline.tsx.
 *
 * **Photographs are still added from the litter's page**, which is why there is
 * no Add button on this line: a photographing is one dated session covering the
 * whole litter (see components/PhotoWizard.tsx), and the litter is one tap away
 * in the breadcrumb. The other picture of this puppy — the profile picture, the
 * one that identifies it — is set in the editor below and is not part of the
 * archive.
 *
 * `/offspring/$offspringId` and not `/litters/$litterId/$offspringId`: offspring
 * hang off the tenant and not off their litter (see api/offspring.ts), the id
 * alone identifies one, and a two-parameter path would be a second copy of the
 * litter id that a link could get wrong.
 */
import {
  canSeePlannedLitters,
  canView,
  canWriteLitters,
  type Offspring,
  type OffspringStatus,
} from '@puppy-doctor/platform';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Pencil } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useLitter } from '@/api/litters.ts';
import { deleteOffspring, updateOffspring } from '@/api/mutations.ts';
import { inLitter, useOffspring } from '@/api/offspring.ts';
import { usePhotoSessions } from '@/api/photoSessions.ts';
import { Breadcrumbs } from '@/components/Breadcrumbs.tsx';
import { useConfirm } from '@/components/Confirm.tsx';
import { PhotoTimeline } from '@/components/gallery/PhotoTimeline.tsx';
import { GrowthSection } from '@/components/growth/GrowthSection.tsx';
import { birthWeightOf, type Draft, DraftCard, draftOf } from '@/components/OffspringDraft.tsx';
import { Photo } from '@/components/Photo.tsx';
import { Button, buttonClasses, Select } from '@/components/ui.tsx';
import { collarName } from '@/lib/collars.ts';
import { useOffspringName } from '@/lib/names.ts';
import { useUrlPanel } from '@/lib/useUrlPanel.ts';
import { useAccess, useTenantId } from '@/tenant/TenantProvider.tsx';

export const Route = createFileRoute('/offspring/$offspringId')({
  component: OffspringPage,
});

const EDIT_PARAM = 'edit';

const STATUSES: readonly OffspringStatus[] = [
  'raising',
  'reserved',
  'placed',
  'kept',
  'deceased',
];

/**
 * Resolved from the one collection-wide listener the whole app shares, rather
 * than a document listener of its own: the litter this puppy is in has already
 * subscribed to every offspring in the tenant, so arriving here from a tile
 * costs nothing and works with no network. See api/offspring.ts.
 */
function OffspringPage() {
  const { t } = useTranslation();
  const { offspringId } = Route.useParams();
  const tenantId = useTenantId();

  const { data: all, isPending } = useOffspring(tenantId);
  const young = all?.find((one) => one.id === offspringId) ?? null;

  if (isPending) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumbs links={[{ label: t('nav.litters'), to: '/' }]} current={t('common.loading')} />
        <p className="text-ink-faint">{t('common.loading')}</p>
      </div>
    );
  }

  // Not a loading state: a link followed from a stale cache to a puppy somebody
  // else deleted lands here, and it must say so rather than spin.
  if (young === null) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumbs links={[{ label: t('nav.litters'), to: '/' }]} current={t('offspring.missing')} />
        <p className="text-ink-faint">{t('offspring.missing')}</p>
      </div>
    );
  }

  return (
    <Found
      // Remounts — and so re-seeds the editor's draft — when the route moves to
      // a sibling without unmounting.
      key={young.id}
      tenantId={tenantId}
      young={young}
      siblings={inLitter(all, young.litterId)}
    />
  );
}

function Found({
  tenantId,
  young,
  siblings,
}: {
  tenantId: string;
  young: Offspring;
  /** The whole litter, this one included — the collar picker needs the rest. */
  siblings: readonly Offspring[];
}) {
  const { t } = useTranslation();
  const { role, scopes } = useAccess();
  const { data: litter } = useLitter(tenantId, young.litterId);

  const [editing, setEditing] = useState(false);

  // So the back button closes the editor the same way Cancel does. See
  // lib/useUrlPanel.ts.
  const editPanel = useUrlPanel(EDIT_PARAM, editing, () => setEditing(false));

  const mayEdit = role !== null && canWriteLitters(role);
  /**
   * The growth section here reads the same weighings the litter's page does, so
   * it is allowed on the same terms — a viewer invited to watch the photographs
   * and not the numbers is refused this collection by the security rules, and a
   * refused listener fails terminally. See `canView` in platform/src/roles.ts.
   */
  const mayReadWeights = role !== null && canView(role, scopes, 'measurements');
  /**
   * And the photographs on the same terms, for the same reason: a viewer scoped
   * to the weights alone is refused this collection by the rules, and a refused
   * listener fails terminally — so this decides whether the listener opens at
   * all, not whether a section is hidden after it has loaded.
   */
  const mayReadPhotos = role !== null && canView(role, scopes, 'photos');
  /**
   * The litter's sessions, from the same collection-wide listener the litter's
   * own page opens — arriving here from that page costs no request and works
   * with the radio off. The filtering down to this puppy is the timeline's, not
   * the query's: a photographing is stored per litter, and a per-puppy query
   * does not exist to be asked. See api/photoSessions.ts.
   */
  const { data: sessions } = usePhotoSessions(tenantId, young.litterId, mayReadPhotos);
  const collar = collarName(young.markerColor);
  // A puppy with no name is called by its collar, because that is what the
  // breeder is calling it out loud. See lib/names.ts.
  const called = useOffspringName()(young);

  const litterKnown = litter !== null && litter !== undefined;

  /**
   * A puppy of a litter that is still only PLANNED is not a viewer's to read,
   * for the reason the litter's own page gives — see `canSeePlannedLitters`. The
   * same dead end as a puppy somebody deleted, and deliberately the same
   * sentence: a different one would confirm the litter exists.
   *
   * Only once the litter is known. Until then there is nothing to judge, and
   * refusing on a pending read would flash "not here" at everybody.
   */
  if (
    litterKnown &&
    litter.status === 'incoming' &&
    !(role !== null && canSeePlannedLitters(role))
  ) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumbs
          links={[{ label: t('nav.litters'), to: '/' }]}
          current={t('offspring.missing')}
        />
        <p className="text-ink-faint">{t('offspring.missing')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5">
      <Breadcrumbs
        links={
          litterKnown
            ? [
                { label: t('nav.litters'), to: '/' },
                { label: litter.name, to: '/litters/$litterId', litterId: young.litterId },
              ]
            : [{ label: t('nav.litters'), to: '/' }]
        }
        current={called}
      />

      <section className="flex items-start gap-4">
        <span
          // Content, not chrome: the collar is drawn as the real ribbon
          // colour, because that is how the reader matches this screen to
          // the puppy in their hand. See lib/collars.ts.
          style={{ borderColor: young.markerColor }}
          className="block shrink-0 rounded-2xl border-4"
        >
          <Photo path={young.photoPath} alt="" className="size-28 rounded-xl" />
        </span>

        <div className="min-w-0 flex-1">
          <div className="flex items-start gap-2">
            <h1 className="min-w-0 flex-1 truncate text-2xl font-bold tracking-[-0.02em]">
              {called}
            </h1>
            {mayEdit && !editing && (
              <Button
                variant="quiet"
                aria-label={t('offspring.edit', { name: called })}
                className="min-h-tap w-11 shrink-0 px-0"
                onClick={() => {
                  setEditing(true);
                  editPanel.markOpen();
                }}
              >
                <Pencil className="size-4" aria-hidden />
              </Button>
            )}
          </div>
          <p className="mt-1 text-sm text-ink-soft">
            {t(`sex.${young.sex}`)}
            <span aria-hidden> · </span>
            {t(`offspring.status.${young.status}`)}
          </p>
          {collar !== null && (
            <p className="mt-0.5 text-sm text-ink-faint">
              {t('offspring.collarLabel')}
              <span aria-hidden> · </span>
              {t(`collar.${collar}`)}
            </p>
          )}
          {/*
            The one weight that is a property of the animal rather than of a
            weighing, so it belongs up here with the sex and the collar and not
            in the growth section, which is about what has happened since. Said
            even when it is missing: the growth model's first-week checks are all
            measured from it, and a blank where a number should be explains why
            the section below has less to say than it might.
          */}
          <p className="mt-0.5 text-sm text-ink-faint">
            {t('offspring.birthWeightLabel')}
            <span aria-hidden> · </span>
            {young.birthWeightGrams === null
              ? t('offspring.birthWeightMissing')
              : t('growth.grams', { grams: Math.round(young.birthWeightGrams) })}
          </p>
        </div>
      </section>

      {editing && (
        <Editor
          tenantId={tenantId}
          young={young}
          siblings={siblings}
          // Unknown litter means unknown status, and the safe reading is the
          // ordinary one: a puppy with a page of its own has been born.
          needsBirthWeight={!litterKnown || litter.status !== 'incoming'}
          onClose={() => {
            setEditing(false);
            editPanel.markClosed();
          }}
        />
      )}

      {/*
        The litter's section with one puppy in it — same arithmetic, same chart,
        same table, no legend and no names, because the heading above already
        says whose weights these are. See components/growth/GrowthSection.tsx.

        Only once the litter is known: every day on the x axis is counted from
        its date of birth, and there is no honest chart to draw without one.
      */}
      {litterKnown && mayReadWeights && (
        <GrowthSection
          tenantId={tenantId}
          litter={litter}
          offspring={[young]}
          action={
            // A link and not a button: it goes somewhere, and the litter's own
            // page is where the size class is set. See the note on
            // `buttonClasses` in ui.tsx.
            <Link
              to="/litters/$litterId"
              params={{ litterId: young.litterId }}
              className={buttonClasses({
                variant: 'secondary',
                className: 'min-h-tap self-start px-4 text-sm',
              })}
            >
              {t('growth.openLitter')}
            </Link>
          }
        />
      )}

      {/*
        This puppy's own photographic line: the litter's archive read through
        one subject. No Add button — see the note at the top of this file — and
        only once the litter is known, because every point on the axis is a day
        counted from its date of birth.
      */}
      {litterKnown && mayReadPhotos && (
        <PhotoTimeline litter={litter} sessions={sessions ?? []} subject={young} />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Correcting this one.
 *
 * The same card the wizard and the litter page use for a new puppy, so the three
 * cannot drift — see components/OffspringDraft.tsx — plus the one field only a
 * puppy that already exists can have: where it has got to. Status is a `Select`
 * and not the segmented control the rest of the app uses, because five options
 * do not fit across a phone and the answer is not something a reader needs to
 * see all the alternatives to.
 */
function Editor({
  tenantId,
  young,
  siblings,
  needsBirthWeight,
  onClose,
}: {
  tenantId: string;
  young: Offspring;
  siblings: readonly Offspring[];
  needsBirthWeight: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const called = useOffspringName()(young);

  const [draft, setDraft] = useState<Draft>(() => draftOf(young));
  const [status, setStatus] = useState<OffspringStatus>(young.status);
  const [showErrors, setShowErrors] = useState(false);

  const save = () => {
    const birthWeightGrams = birthWeightOf(draft);
    if (
      draft.sex === null ||
      draft.markerColor === null ||
      (needsBirthWeight && birthWeightGrams === null)
    ) {
      setShowErrors(true);
      return;
    }

    // A patch of exactly the fields this form owns. Never a whole-document
    // write: `microchip` and `notes` are not on this screen, and a full
    // overwrite would clear whatever another device put in them. See the note on
    // `toFirestore` in api/converter.ts.
    updateOffspring(tenantId, young.id, {
      name: draft.name.trim(),
      sex: draft.sex,
      markerColor: draft.markerColor,
      birthWeightGrams,
      status,
      photoPath: draft.photoPath,
    });

    onClose();
  };

  return (
    <div className="flex flex-col gap-3">
      <DraftCard
        tenantId={tenantId}
        draft={draft}
        index={young.sequence}
        taken={siblings
          .filter((other) => other.id !== young.id)
          .map((other) => other.markerColor)}
        needsBirthWeight={needsBirthWeight}
        showErrors={showErrors}
        onChange={(change) => setDraft({ ...draft, ...change })}
        // The most destructive act in the app: a puppy takes its weights, its
        // health events and its photographs with it, and none of it comes back.
        onRemove={() =>
          confirm({
            title: t('offspring.deleteTitle', { name: called }),
            body: t('offspring.deleteBody'),
            confirmLabel: t('common.delete'),
            onConfirm: () => {
              deleteOffspring(tenantId, young.id);
              // Nothing to await: this screen is about to be a puppy that no
              // longer exists, and the litter's listener already knows.
              void navigate({ to: '/litters/$litterId', params: { litterId: young.litterId } });
            },
          })
        }
      />

      <Select
        label={t('offspring.statusLabel')}
        value={status}
        onChange={(event) =>
          setStatus(STATUSES.find((option) => option === event.target.value) ?? status)
        }
      >
        {STATUSES.map((option) => (
          <option key={option} value={option}>
            {t(`offspring.status.${option}`)}
          </option>
        ))}
      </Select>

      <div className="flex gap-2">
        <Button full onClick={save}>
          {t('common.save')}
        </Button>
        <Button variant="secondary" className="px-5" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </div>
    </div>
  );
}
